import { ApiError } from './types';
import { normaliseAdded, normaliseChanges, normaliseRepeatRuns, normaliseRuns, normaliseSummary } from '../repeat-runs/model';
import { parseLeadKey, type Platform } from '../lib/platform';
import {
  normaliseBulkResult,
  normaliseOutcomes,
  normaliseSuggestionList,
  NO_OUTCOMES,
  type BulkResult,
  type Outcomes,
  type SuggestionList,
  type SuggestionQuery,
} from '../suggestions/model';
import type {
  AddUsernamesResult,
  Capabilities,
  RepostAnalysis,
  RepostRunInput,
  RepostRunResult,
  SearchSchedule,
  RepeatRunsResponse,
  ScheduleAddedResponse,
  ScheduleRunsResponse,
  RunAddedResponse,
  ResolvedSound,
  SoundInfo,
  SuggestedSoundsResponse,
  SoundSuggestionInput,
  SoundSuggestionResult,
  GeoCatalog,
  TermSuggestResult,
  GenerationRun,
  SelectableCampaign,
  SuggestionListResponse,
  SuggestionApproveInput,
  SuggestionFilters,
  AccountCreateInput,
  AccountSummary,
  BulkExportResponse,
  CampaignCreateInput,
  CampaignLive,
  CampaignPoll,
  Creator,
  CreatorListFilters,
  CreatorListResponse,
  DedupeStatusResponse,
  Folder,
  FolderCreateInput,
  FolderGuidance,
  FolderUpdateInput,
  LookalikeAnalysis,
  LookalikeRunInput,
  LookalikeRunResult,
  LookalikeSeedCounts,
  SearchPreset,
  OutreachStateCounts,
  JobSummary,
  Lead,
  LeadEnrichment,
  LeadPipelineStage,
  OutreachInviter,
  OutreachTeam,
  ScrapeJob,
  ScrapeJobPoll,
  ScrapeParams,
  SearchHistoryEntry,
  // ── workspace (agent A) ──
  CreatorExportInput,
  FolderExportInput,
  GenerationStatus,
  LeadListQuery,
  LeadMarkAction,
  LeadPage,
  ReassignBody,
  ReassignResult,
  SettingsValuesResponse,
} from './types';

import { filterQuery } from '../email/model';
import type {
  AudienceFilter,
  AudienceOrder,
  AudienceRow,
  AudienceSummary,
  BatchInput,
  BatchResult,
  Breakdowns,
  CampaignDetail,
  EmailCampaign,
  EmailCampaignWithFunnel,
  EmailStatus,
  Funnel,
  InstantlyCampaignOption,
  NewCampaignInput,
  Recipient,
  RecipientQuery,
  TrackingStatus,
} from '../email/types';

export type GetToken = () => Promise<string | null>;

export interface ClientOptions {
  apiUrl: string;
  getToken: GetToken;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
  expectBlob?: boolean;
}

export interface DiscoveryClient {
  /**
   * Clipping campaigns available to attach to a folder. Named distinctly from
   * `campaigns`, which is this service's own DM-campaign resource.
   */
  clippingCampaigns: {
    list(): Promise<{ campaigns: SelectableCampaign[]; available: boolean }>;
  };
  suggestions: {
    list(status?: string): Promise<SuggestionListResponse>;
    filterDefaults(): Promise<{ defaults: SuggestionFilters }>;
    /** Starts a pass and returns immediately; poll `generationStatus`. */
    generate(): Promise<{ started: boolean; alreadyRunning: boolean; status: GenerationRun }>;
    generationStatus(): Promise<GenerationRun>;
    approve(
      publicId: string,
      input: SuggestionApproveInput,
    ): Promise<{ jobId: string; folder: { publicId: string; name: string } }>;
    reject(publicId: string, reason?: string): Promise<unknown>;
    /**
     * One page of suggestions, filtered and sorted (SCALE contract V2). Works
     * against the older endpoint too — see `normaliseSuggestionList`.
     */
    query(query: SuggestionQuery): Promise<SuggestionList>;
    /** Approve several with the same options. Falls back to one request each on a server without the bulk endpoint. */
    bulkApprove(ids: string[], input: SuggestionApproveInput, onProgress?: (done: number, total: number) => void): Promise<BulkResult>;
    bulkReject(ids: string[], reason?: string, onProgress?: (done: number, total: number) => void): Promise<BulkResult>;
    /** Send one sound to the queue as a pending suggestion (from a Lookalike analysis). */
    fromSound(input: SoundSuggestionInput): Promise<SoundSuggestionResult>;
  };
  insights: {
    /** Outreach results per folder and per schedule. `available: false` on a server without the endpoint. */
    outcomes(): Promise<Outcomes>;
  };
  folders: {
    list(options?: { includeArchived?: boolean; search?: string }): Promise<Folder[]>;
    create(input: FolderCreateInput): Promise<Folder>;
    update(publicId: string, patch: FolderUpdateInput): Promise<Folder>;
    /** `confirmLeadCount` must match the folder's real count when non-empty. */
    delete(publicId: string, confirmLeadCount?: number): Promise<{ deletedLeads: number }>;
    /** Paste handles into a folder. Starts a job; each one is enriched. */
    addUsernames(publicId: string, usernames: string, platform?: Platform): Promise<AddUsernamesResult>;
    /** Who this folder's creators repost. One API call per seed, no enrichment. */
    analyzeReposts(
      publicId: string,
      opts?: { maxSeeds?: number; topicTerms?: string[] },
    ): Promise<RepostAnalysis>;
    runReposts(publicId: string, input: RepostRunInput): Promise<RepostRunResult>;
    /** Harvest + score hashtags from this folder's own creators. Costs API calls. */
    lookalikeSeeds(publicId: string): Promise<LookalikeSeedCounts>;
    analyzeLookalikes(publicId: string, opts?: { maxSeeds?: number }): Promise<LookalikeAnalysis>;
    runLookalike(publicId: string, input: LookalikeRunInput): Promise<LookalikeRunResult>;
    getGuidance(publicId: string): Promise<FolderGuidance | null>;
    saveGuidance(
      publicId: string,
      body: {
        recommendedCampaigns: string | null;
        campaignIds?: string[];
        pitchNotes: string | null;
      },
    ): Promise<FolderGuidance>;
    searchHistory(publicId: string): Promise<SearchHistoryEntry[]>;
    recordSearch(publicId: string, params: ScrapeParams): Promise<void>;
    activeJob(publicId: string): Promise<{ jobId: string | null; status: string | null }>;
    exportToOutreach(
      publicId: string,
      input?: {
        leadIds?: number[];
        niches?: string[];
        suggestedCategory?: string;
        assignToInviterId?: number;
        assignToTeam?: string;
      },
    ): Promise<BulkExportResponse>;
    dedupeStatus(publicId: string): Promise<DedupeStatusResponse>;
  };
  outreach: {
    listInviters(): Promise<OutreachInviter[]>;
    listTeams(): Promise<OutreachTeam[]>;
  };
  presets: {
    list(): Promise<SearchPreset[]>;
    create(input: { name: string; description?: string; params: Record<string, unknown> }): Promise<SearchPreset>;
    update(publicId: string, patch: { name?: string; description?: string | null }): Promise<SearchPreset>;
    delete(publicId: string): Promise<void>;
  };
  leads: {
    list(folderPublicId: string): Promise<Lead[]>;
    delete(folderPublicId: string, username: string): Promise<void>;
    deleteBulk(folderPublicId: string, usernames: string[]): Promise<{ deleted: number }>;
    saveBatch(folderPublicId: string, creators: LeadEnrichment[]): Promise<{ saved: number }>;
    markDmSent(folderPublicId: string, username: string): Promise<void>;
    update(
      folderPublicId: string,
      username: string,
      patch: { pipeline?: LeadPipelineStage; notes?: string },
    ): Promise<void>;
    exportCsv(folderPublicId: string): Promise<Blob>;
  };
  /** Team-wide creator registry — one row per creator, with folders + status. */
  /** Email outreach: segment creators with addresses, send to Instantly or CSV, read results. */
  email: {
    status(): Promise<EmailStatus>;
    connectTracking(): Promise<TrackingStatus>;
    audienceSummary(filter: AudienceFilter): Promise<AudienceSummary>;
    audience(
      filter: AudienceFilter,
      page?: { limit?: number; offset?: number; order?: AudienceOrder },
    ): Promise<{ rows: AudienceRow[]; limit: number; offset: number }>;
    /** A one-off download that records nothing. */
    exportAudienceCsv(filter: AudienceFilter, opts?: { limit?: number; order?: AudienceOrder; linkTemplate?: string }): Promise<Blob>;
    instantlyCampaigns(): Promise<{ campaigns: InstantlyCampaignOption[] }>;
    campaigns(opts?: { includeArchived?: boolean }): Promise<{ campaigns: EmailCampaignWithFunnel[]; totals: Funnel }>;
    overview(): Promise<{ totals: Funnel; breakdowns: Breakdowns }>;
    createCampaign(input: NewCampaignInput): Promise<BatchResult>;
    campaign(publicId: string): Promise<CampaignDetail>;
    updateCampaign(
      publicId: string,
      patch: { name?: string; notes?: string; linkTemplate?: string; archived?: boolean },
    ): Promise<{ campaign: EmailCampaign }>;
    addToCampaign(publicId: string, input: BatchInput): Promise<BatchResult>;
    retryCampaign(publicId: string): Promise<BatchResult>;
    syncCampaign(publicId: string): Promise<{ leads: number; updated: number; joined: number; funnel: Funnel }>;
    recipients(publicId: string, query?: RecipientQuery): Promise<{ recipients: Recipient[]; total: number; limit: number; offset: number }>;
    exportCampaignCsv(publicId: string): Promise<Blob>;
  };
  /** What the server has switched on. Cheap, cacheable, changes only on deploy. */
  capabilities(): Promise<Capabilities>;
  /** Resolve a pasted TikTok sound link (or bare id) to an id plus its name. */
  resolveSound(input: string): Promise<ResolvedSound>;
  sounds: {
    /**
     * Names, covers and preview URLs from the sound catalogue, max 100 per
     * call. Throws ApiError 404 on a server without the catalogue.
     */
    lookup(refs: { platform: 'tiktok' | 'instagram'; id: string }[]): Promise<SoundInfo[]>;
    /** Sounds to add to a sound search. Topic words cost 1–2 search calls; without them it's free. */
    suggest(input: { exclude?: string[]; topicTerms?: string[] }): Promise<SuggestedSoundsResponse>;
  };
  geo: {
    /** Every country the audience picker offers, plus preset groups. */
    countries(): Promise<GeoCatalog>;
    /** Search terms that pull these topics into these countries, each measured. */
    suggestTerms(topics: string[], countries: string[]): Promise<TermSuggestResult>;
  };
  schedules: {
    list(folderPublicId: string): Promise<SearchSchedule[]>;
    create(
      folderPublicId: string,
      input: { name: string; everyDays: number; params: ScrapeParams },
    ): Promise<SearchSchedule>;
    update(
      publicId: string,
      patch: { enabled?: boolean; everyDays?: number; name?: string },
    ): Promise<SearchSchedule>;
    remove(publicId: string): Promise<{ deleted: boolean }>;
    /** Every schedule in the team, across folders, with health and recent runs. */
    listAll(): Promise<RepeatRunsResponse>;
    /** One page of a schedule's run history, newest first. */
    runs(publicId: string, page?: number): Promise<ScheduleRunsResponse>;
    /** One page of every creator the schedule's runs added, newest first. */
    added(publicId: string, page?: number): Promise<ScheduleAddedResponse>;
    /** All of them as a CSV. */
    exportAdded(publicId: string): Promise<Blob>;
  };
  registry: {
    list(filters?: CreatorListFilters): Promise<CreatorListResponse>;
    counts(): Promise<OutreachStateCounts>;
    refreshStatus(usernames: string[]): Promise<{ updated: number }>;
    suppress(username: string, reason?: string): Promise<void>;
    unsuppress(username: string): Promise<void>;
    removeFromAllFolders(username: string): Promise<{ removed: number }>;
  };
  accounts: {
    list(): Promise<AccountSummary[]>;
    create(input: AccountCreateInput): Promise<{ id: string }>;
    update(publicId: string, patch: AccountCreateInput): Promise<void>;
    delete(publicId: string): Promise<void>;
    setupLocal(accountId: string): Promise<void>;
    liveSession(proxy?: string): Promise<{ sessionId: string; contextId: string; liveViewUrl: string }>;
  };
  campaigns: {
    list(): Promise<CampaignLive[]>;
    get(id: string): Promise<CampaignLive>;
    poll(id: string, since: number): Promise<CampaignPoll>;
    create(input: CampaignCreateInput): Promise<{ campaignId: string; totalLeads: number }>;
    stop(id: string): Promise<void>;
  };
  scrape: {
    start(params: ScrapeParams): Promise<{ jobId: string }>;
  };
  jobs: {
    list(): Promise<JobSummary[]>;
    get(id: string): Promise<ScrapeJob>;
    poll(id: string, since: number, sinceLeads?: number): Promise<ScrapeJobPoll>;
    stop(id: string): Promise<void>;
    /** The creators one run put in its folder. */
    added(id: string, page?: number): Promise<RunAddedResponse>;
  };
  // ── workspace (agent A) ──
  workspace: {
    /** One page of a folder's leads, filtered and sorted server-side. */
    leadsPage(folderPublicId: string, query: LeadListQuery): Promise<LeadPage>;
    markLeads(
      folderPublicId: string,
      usernames: string[],
      action: LeadMarkAction,
    ): Promise<{ updated: number }>;
    reassignFolder(folderPublicId: string, body: ReassignBody): Promise<ReassignResult>;
    reassignCreators(body: ReassignBody): Promise<ReassignResult>;
    exportFolder(folderPublicId: string, input: FolderExportInput): Promise<BulkExportResponse>;
    /** Not in the shared contract yet — see CreatorExportInput. */
    exportCreators(input: CreatorExportInput): Promise<BulkExportResponse>;
    settingsValues(): Promise<SettingsValuesResponse>;
    generationStatus(): Promise<GenerationStatus>;
  };
}

export function createClient({ apiUrl, getToken }: ClientOptions): DiscoveryClient {
  const base = apiUrl.replace(/\/+$/, '');

  function buildUrl(path: string, query?: RequestOptions['query']): string {
    const suffix = path.startsWith('/') ? path : `/${path}`;
    const url = `${base}${suffix}`;
    if (!query) return url;
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null) params.set(k, String(v));
    }
    const qs = params.toString();
    return qs ? `${url}?${qs}` : url;
  }

  async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const token = await getToken();
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;

    let body: string | undefined;
    if (options.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(options.body);
    }

    const res = await fetch(buildUrl(path, options.query), {
      method: options.method ?? 'GET',
      headers,
      body,
      signal: options.signal,
    });

    if (options.expectBlob) {
      if (!res.ok) {
        const text = await res.text();
        throw new ApiError(res.status, null, text || `Request failed (${res.status})`);
      }
      return (await res.blob()) as unknown as T;
    }

    const text = await res.text();
    const parsed: unknown = text ? safeJson(text) : null;

    if (!res.ok) {
      const message = isErrorPayload(parsed)
        ? parsed.error
        : text || `Request failed (${res.status})`;
      const code = isErrorPayload(parsed) ? parsed.code ?? null : null;
      throw new ApiError(res.status, code, message);
    }

    return parsed as T;
  }

  // The contract names the V2 list `/api/suggestions`; the endpoint it grew out
  // of is `/api/search-suggestions`. Ask the new one first and remember which
  // answered, so an older server costs one extra request per page load, not one per query.
  const SUGGESTION_PATHS = ['/api/suggestions', '/api/search-suggestions'] as const;
  let suggestionsPath: string | null = null;
  const missing = (err: unknown) => err instanceof ApiError && (err.status === 404 || err.status === 405);

  async function querySuggestions(query: SuggestionQuery): Promise<SuggestionList> {
    const last = (path: string) => path === SUGGESTION_PATHS[SUGGESTION_PATHS.length - 1];
    const params = {
      status: query.status || 'pending',
      category: query.category || undefined,
      sort: query.sort,
      dir: query.dir,
      platform: query.platform || undefined,
      q: query.q.trim() || undefined,
      page: query.page,
    };
    for (const path of suggestionsPath ? [suggestionsPath] : SUGGESTION_PATHS) {
      try {
        const raw = await request<unknown>(path, { query: params });
        // Anything but a suggestions payload (a proxy's catch-all page, say) isn't this endpoint.
        const shaped =
          typeof raw === 'object' && raw !== null && ['rows', 'items'].some((k) => Array.isArray((raw as Record<string, unknown>)[k]));
        if (!shaped && !last(path)) continue;
        suggestionsPath = path;
        return normaliseSuggestionList(raw, query);
      } catch (err) {
        if (!missing(err) || last(path)) throw err;
      }
    }
    throw new ApiError(404, null, 'Suggestions are not available');
  }

  /** The bulk endpoint, or the single one per id where the server has no bulk. */
  async function bulkSuggestions(
    action: 'approve' | 'reject',
    ids: string[],
    body: Record<string, unknown>,
    onProgress?: (done: number, total: number) => void,
  ): Promise<BulkResult> {
    try {
      const raw = await request<unknown>(`${suggestionsPath ?? '/api/search-suggestions'}/bulk-${action}`, {
        method: 'POST',
        body: { ...body, ids },
      });
      onProgress?.(ids.length, ids.length);
      return normaliseBulkResult(raw, ids);
    } catch (err) {
      if (!missing(err)) throw err;
    }
    const result: BulkResult = { done: [], failed: [] };
    for (const [i, id] of ids.entries()) {
      try {
        await request<unknown>(`/api/search-suggestions/${encodeURIComponent(id)}/${action}`, { method: 'POST', body });
        result.done.push(id);
      } catch (err) {
        result.failed.push({ id, message: (err as Error).message });
      }
      onProgress?.(i + 1, ids.length);
    }
    return result;
  }

  return {
    clippingCampaigns: {
      list: () =>
        request<{ campaigns: SelectableCampaign[]; available: boolean }>('/api/campaigns'),
    },
    suggestions: {
      filterDefaults: () =>
        request<{ defaults: SuggestionFilters }>('/api/search-suggestions/filter-defaults'),
      list: (status = 'pending') =>
        request<SuggestionListResponse>(
          `/api/search-suggestions?status=${encodeURIComponent(status)}`,
        ),
      generate: () =>
        request<{ started: boolean; alreadyRunning: boolean; status: GenerationRun }>(
          '/api/search-suggestions/generate',
          { method: 'POST' },
        ),
      generationStatus: () => request<GenerationRun>('/api/search-suggestions/generation'),
      approve: (publicId, input) =>
        request<{ jobId: string; folder: { publicId: string; name: string } }>(
          `/api/search-suggestions/${encodeURIComponent(publicId)}/approve`,
          { method: 'POST', body: input },
        ),
      reject: (publicId, reason) =>
        request<unknown>(`/api/search-suggestions/${encodeURIComponent(publicId)}/reject`, {
          method: 'POST',
          body: { reason },
        }),
      query: querySuggestions,
      bulkApprove: (ids, input, onProgress) => bulkSuggestions('approve', ids, { ...input }, onProgress),
      bulkReject: (ids, reason, onProgress) => bulkSuggestions('reject', ids, { reason }, onProgress),
      fromSound: (input) =>
        request<SoundSuggestionResult>('/api/suggestions/from-sound', { method: 'POST', body: input }),
    },
    insights: {
      outcomes: async () => {
        try {
          return normaliseOutcomes(await request<unknown>('/api/insights/outcomes'));
        } catch (err) {
          // An older Discovery service: no chips, rather than an error on every folder.
          if (missing(err)) return NO_OUTCOMES;
          throw err;
        }
      },
    },
    folders: {
      list: (options) =>
        request<Folder[]>('/api/folders', {
          query: { includeArchived: options?.includeArchived, search: options?.search },
        }),
      create: (input) => request<Folder>('/api/folders', { method: 'POST', body: input }),
      update: (publicId, patch) =>
        request<Folder>(`/api/folders/${encodeURIComponent(publicId)}`, {
          method: 'PATCH',
          body: patch,
        }),
      delete: (publicId, confirmLeadCount) =>
        request<{ deletedLeads: number }>(`/api/folders/${encodeURIComponent(publicId)}`, {
          method: 'DELETE',
          query: { confirmLeadCount },
        }),
      addUsernames: (publicId, usernames, platform) =>
        request<AddUsernamesResult>(`/api/folders/${encodeURIComponent(publicId)}/leads/usernames`, {
          method: 'POST',
          body: { usernames, platform },
        }),
      analyzeReposts: (publicId, opts) =>
        request<RepostAnalysis>(`/api/folders/${encodeURIComponent(publicId)}/reposts/analyze`, {
          method: 'POST',
          body: opts ?? {},
        }),
      runReposts: (publicId, input) =>
        request<RepostRunResult>(`/api/folders/${encodeURIComponent(publicId)}/reposts/run`, {
          method: 'POST',
          body: input,
        }),
      lookalikeSeeds: (publicId) =>
        request<LookalikeSeedCounts>(`/api/folders/${encodeURIComponent(publicId)}/lookalike/seeds`),
      analyzeLookalikes: (publicId, opts) =>
        request<LookalikeAnalysis>(`/api/folders/${encodeURIComponent(publicId)}/lookalike/analyze`, {
          method: 'POST',
          body: opts ?? {},
        }),
      runLookalike: (publicId, input) =>
        request<LookalikeRunResult>(`/api/folders/${encodeURIComponent(publicId)}/lookalike/run`, {
          method: 'POST',
          body: input,
        }),
      getGuidance: (publicId) =>
        request<FolderGuidance | null>(`/api/folders/${encodeURIComponent(publicId)}/guidance`),
      saveGuidance: (publicId, body) =>
        request<FolderGuidance>(`/api/folders/${encodeURIComponent(publicId)}/guidance`, {
          method: 'PUT',
          body,
        }),
      searchHistory: (publicId) =>
        request<SearchHistoryEntry[]>(`/api/folders/${encodeURIComponent(publicId)}/search-history`),
      recordSearch: (publicId, params) =>
        request<void>(`/api/folders/${encodeURIComponent(publicId)}/search-history`, {
          method: 'POST',
          body: params,
        }),
      activeJob: (publicId) =>
        request<{ jobId: string | null; status: string | null }>(`/api/folders/${encodeURIComponent(publicId)}/active-job`),
      exportToOutreach: (publicId, input) =>
        request<BulkExportResponse>(
          `/api/folders/${encodeURIComponent(publicId)}/export-to-outreach`,
          { method: 'POST', body: input ?? {} },
        ),
      dedupeStatus: (publicId) =>
        request<DedupeStatusResponse>(
          `/api/folders/${encodeURIComponent(publicId)}/dedupe-status`,
        ),
    },
    leads: {
      // Contract §3 turned this into `{ rows, total }`; callers here still want
      // the rows (the search panel reads the folder's platform mix from them).
      list: async (folderPublicId) => {
        const res = await request<Lead[] | LeadPage>(
          `/api/folders/${encodeURIComponent(folderPublicId)}/leads`,
          { query: { limit: 500 } },
        );
        return Array.isArray(res) ? res : res?.rows ?? [];
      },
      delete: (folderPublicId, key) => {
        const { platform, username } = parseLeadKey(key);
        return request<void>(
          `/api/folders/${encodeURIComponent(folderPublicId)}/leads/${encodeURIComponent(username)}`,
          { method: 'DELETE', query: { platform } },
        );
      },
      deleteBulk: (folderPublicId, usernames) =>
        request<{ deleted: number }>(
          `/api/folders/${encodeURIComponent(folderPublicId)}/leads/bulk-delete`,
          { method: 'POST', body: { usernames } },
        ),
      saveBatch: (folderPublicId, creators) =>
        request<{ saved: number }>(
          `/api/folders/${encodeURIComponent(folderPublicId)}/leads/batch`,
          { method: 'POST', body: { creators } },
        ),
      markDmSent: (folderPublicId, key) => {
        const { platform, username } = parseLeadKey(key);
        return request<void>(
          `/api/folders/${encodeURIComponent(folderPublicId)}/leads/${encodeURIComponent(username)}/dm-sent`,
          { method: 'POST', query: { platform } },
        );
      },
      update: (folderPublicId, key, patch) => {
        const { platform, username } = parseLeadKey(key);
        return request<void>(
          `/api/folders/${encodeURIComponent(folderPublicId)}/leads/${encodeURIComponent(username)}`,
          { method: 'PATCH', body: patch, query: { platform } },
        );
      },
      exportCsv: (folderPublicId) =>
        request<Blob>(`/api/folders/${encodeURIComponent(folderPublicId)}/export`, {
          expectBlob: true,
        }),
    },
    email: {
      status: () => request<EmailStatus>('/api/email/status'),
      connectTracking: () => request<TrackingStatus>('/api/email/tracking/connect', { method: 'POST' }),
      audienceSummary: (filter) =>
        request<AudienceSummary>('/api/email/audience/summary', { query: filterQuery(filter) }),
      audience: (filter, page) =>
        request('/api/email/audience', { query: { ...filterQuery(filter), ...page } }),
      exportAudienceCsv: (filter, opts) =>
        request<Blob>('/api/email/audience/export.csv', {
          query: { ...filterQuery(filter), ...opts },
          expectBlob: true,
        }),
      instantlyCampaigns: () => request('/api/email/instantly/campaigns'),
      campaigns: (opts) => request('/api/email/campaigns', { query: { includeArchived: opts?.includeArchived } }),
      overview: () => request('/api/email/overview'),
      createCampaign: (input) => request<BatchResult>('/api/email/campaigns', { method: 'POST', body: input }),
      campaign: (publicId) => request<CampaignDetail>(`/api/email/campaigns/${encodeURIComponent(publicId)}`),
      updateCampaign: (publicId, patch) =>
        request(`/api/email/campaigns/${encodeURIComponent(publicId)}`, { method: 'PATCH', body: patch }),
      addToCampaign: (publicId, input) =>
        request<BatchResult>(`/api/email/campaigns/${encodeURIComponent(publicId)}/add`, { method: 'POST', body: input }),
      retryCampaign: (publicId) =>
        request<BatchResult>(`/api/email/campaigns/${encodeURIComponent(publicId)}/retry`, { method: 'POST' }),
      syncCampaign: (publicId) =>
        request(`/api/email/campaigns/${encodeURIComponent(publicId)}/sync`, { method: 'POST' }),
      recipients: (publicId, query) =>
        request(`/api/email/campaigns/${encodeURIComponent(publicId)}/recipients`, { query: { ...query } }),
      exportCampaignCsv: (publicId) =>
        request<Blob>(`/api/email/campaigns/${encodeURIComponent(publicId)}/export.csv`, { expectBlob: true }),
    },
    capabilities: () => request<Capabilities>('/api/capabilities'),
    resolveSound: (input) =>
      request<ResolvedSound>('/api/sounds/resolve', { method: 'POST', body: { input } }),
    sounds: {
      lookup: async (refs) => {
        const raw = await request<{ sounds?: unknown }>('/api/sounds/lookup', { method: 'POST', body: { sounds: refs } });
        // Anything but a list (an older server's catch-all) is "nothing known".
        return Array.isArray(raw?.sounds) ? (raw.sounds as SoundInfo[]) : [];
      },
      suggest: (input) => request<SuggestedSoundsResponse>('/api/sounds/suggest', { method: 'POST', body: input }),
    },
    geo: {
      countries: () => request<GeoCatalog>('/api/geo/countries'),
      suggestTerms: (topics, countries) =>
        request<TermSuggestResult>('/api/geo/suggest-terms', {
          method: 'POST',
          body: { topics, countries },
        }),
    },
    schedules: {
      list: (folderPublicId) =>
        request<SearchSchedule[]>(`/api/folders/${encodeURIComponent(folderPublicId)}/schedules`),
      create: (folderPublicId, input) =>
        request<SearchSchedule>(`/api/folders/${encodeURIComponent(folderPublicId)}/schedules`, {
          method: 'POST',
          body: input,
        }),
      update: (publicId, patch) =>
        request<SearchSchedule>(`/api/schedules/${encodeURIComponent(publicId)}`, {
          method: 'PATCH',
          body: patch,
        }),
      remove: (publicId) =>
        request<{ deleted: boolean }>(`/api/schedules/${encodeURIComponent(publicId)}`, {
          method: 'DELETE',
        }),
      // Read through the normalisers: an older server sends less (or 404s).
      listAll: async () => normaliseRepeatRuns(await request<unknown>('/api/schedules')),
      runs: async (publicId, page = 1) =>
        normaliseRuns(
          await request<unknown>(`/api/schedules/${encodeURIComponent(publicId)}/runs`, { query: { page } }),
        ),
      added: (publicId, page = 1) =>
        request<ScheduleAddedResponse>(`/api/schedules/${encodeURIComponent(publicId)}/added`, { query: { page } }),
      exportAdded: (publicId) =>
        request<Blob>(`/api/schedules/${encodeURIComponent(publicId)}/added.csv`, { expectBlob: true }),
    },
    registry: {
      list: (filters) =>
        request<CreatorListResponse>('/api/creators-registry', {
          query: filters as Record<string, string | number | boolean | undefined> | undefined,
        }),
      counts: () => request<OutreachStateCounts>('/api/creators-registry/counts'),
      // Outreach answers one channel per request, so keys are grouped by platform.
      refreshStatus: async (keys) => {
        const byPlatform = new Map<Platform, string[]>();
        for (const k of keys) {
          const { platform, username } = parseLeadKey(k);
          byPlatform.set(platform, [...(byPlatform.get(platform) ?? []), username]);
        }
        let updated = 0;
        for (const [platform, usernames] of byPlatform) {
          const r = await request<{ updated: number }>('/api/creators-registry/refresh-status', {
            method: 'POST',
            body: { usernames, platform },
          });
          updated += r.updated ?? 0;
        }
        return { updated };
      },
      suppress: (key, reason) => {
        const { platform, username } = parseLeadKey(key);
        return request<void>(`/api/creators-registry/${encodeURIComponent(username)}/suppress`, {
          method: 'POST',
          body: { reason },
          query: { platform },
        });
      },
      unsuppress: (key) => {
        const { platform, username } = parseLeadKey(key);
        return request<void>(`/api/creators-registry/${encodeURIComponent(username)}/suppress`, {
          method: 'DELETE',
          query: { platform },
        });
      },
      removeFromAllFolders: (key) => {
        const { platform, username } = parseLeadKey(key);
        return request<{ removed: number }>(
          `/api/creators-registry/${encodeURIComponent(username)}/memberships`,
          { method: 'DELETE', query: { platform } },
        );
      },
    },
    accounts: {
      list: () => request<AccountSummary[]>('/api/accounts'),
      create: (input) => request<{ id: string }>('/api/accounts', { method: 'POST', body: input }),
      update: (publicId, patch) =>
        request<void>(`/api/accounts/${encodeURIComponent(publicId)}`, { method: 'PATCH', body: patch }),
      delete: (publicId) =>
        request<void>(`/api/accounts/${encodeURIComponent(publicId)}`, { method: 'DELETE' }),
      setupLocal: (accountId) =>
        request<void>('/api/accounts/setup-local', { method: 'POST', body: { accountId } }),
      liveSession: (proxy) =>
        request<{ sessionId: string; contextId: string; liveViewUrl: string }>(
          '/api/accounts/live-session',
          { method: 'POST', body: { proxy } },
        ),
    },
    campaigns: {
      list: () => request<CampaignLive[]>('/api/campaigns'),
      get: (id) => request<CampaignLive>(`/api/campaigns/${encodeURIComponent(id)}`),
      poll: (id, since) =>
        request<CampaignPoll>(`/api/campaigns/${encodeURIComponent(id)}/poll`, {
          query: { since },
        }),
      create: (input) =>
        request<{ campaignId: string; totalLeads: number }>('/api/campaigns', {
          method: 'POST',
          body: input,
        }),
      stop: (id) =>
        request<void>(`/api/campaigns/${encodeURIComponent(id)}/stop`, { method: 'POST' }),
    },
    outreach: {
      listInviters: () => request<OutreachInviter[]>('/api/outreach/inviters'),
      listTeams: () => request<OutreachTeam[]>('/api/outreach/teams'),
    },
    presets: {
      list: () => request<SearchPreset[]>('/api/search-presets'),
      create: (input) => request<SearchPreset>('/api/search-presets', { method: 'POST', body: input }),
      update: (publicId, patch) =>
        request<SearchPreset>(`/api/search-presets/${encodeURIComponent(publicId)}`, {
          method: 'PATCH',
          body: patch,
        }),
      delete: (publicId) =>
        request<void>(`/api/search-presets/${encodeURIComponent(publicId)}`, { method: 'DELETE' }),
    },
    scrape: {
      start: (params) => request<{ jobId: string }>('/api/scrape', { method: 'POST', body: params }),
    },
    jobs: {
      list: () => request<JobSummary[]>('/api/jobs'),
      get: (id) => request<ScrapeJob>(`/api/jobs/${encodeURIComponent(id)}`),
      poll: (id, since, sinceLeads) =>
        request<ScrapeJobPoll>(`/api/jobs/${encodeURIComponent(id)}/poll`, {
          query: { since, sinceLeads },
        }),
      stop: (id) =>
        request<void>(`/api/jobs/${encodeURIComponent(id)}/stop`, { method: 'POST' }),
      added: async (id, page = 1) =>
        normaliseAdded(await request<unknown>(`/api/jobs/${encodeURIComponent(id)}/added`, { query: { page } }), id),
    },
    // ── workspace (agent A) ──
    workspace: {
      leadsPage: async (folderPublicId, query) => {
        const res = await request<Lead[] | LeadPage>(
          `/api/folders/${encodeURIComponent(folderPublicId)}/leads`,
          { query: query as unknown as RequestOptions['query'] },
        );
        // An older server answers with the whole array and no paging.
        return Array.isArray(res) ? { rows: res, total: res.length } : res;
      },
      markLeads: (folderPublicId, usernames, action) =>
        request<{ updated: number }>(
          `/api/folders/${encodeURIComponent(folderPublicId)}/leads/mark`,
          { method: 'POST', body: { usernames, action } },
        ),
      reassignFolder: (folderPublicId, body) =>
        request<ReassignResult>(`/api/folders/${encodeURIComponent(folderPublicId)}/reassign`, {
          method: 'POST',
          body,
        }),
      reassignCreators: (body) =>
        request<ReassignResult>('/api/creators/reassign', { method: 'POST', body }),
      exportFolder: (folderPublicId, input) =>
        request<BulkExportResponse>(
          `/api/folders/${encodeURIComponent(folderPublicId)}/export-to-outreach`,
          { method: 'POST', body: input },
        ),
      exportCreators: (input) =>
        request<BulkExportResponse>('/api/creators/export-to-outreach', {
          method: 'POST',
          body: input,
        }),
      settingsValues: () => request<SettingsValuesResponse>('/api/settings'),
      generationStatus: () => request<GenerationStatus>('/api/search-suggestions/generation'),
    },
  };
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function isErrorPayload(value: unknown): value is { error: string; code?: string | null } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'error' in value &&
    typeof (value as { error: unknown }).error === 'string'
  );
}

// ── search (agent B) ──
// (Imports live here, not at the top, so this section only ever appends.)
import type {
  FieldErrors,
  IncludeSource,
  ParamChange,
  ParamSummaryEntry,
  SavedSearch,
  SearchJobPoll,
  SearchJobResult,
  SearchSchema,
  SettingsAuditEntry,
  SettingsResponse,
  SettingValue,
} from './types';

// The search form, saved searches, settings and search-job insights
// (contract §1, §2, §5). A separate small client rather than new members on
// DiscoveryClient so it can read 400 `fieldErrors` bodies, which the shared
// `request` drops. Reached through `useSearchApi()` (src/discovery/search/hooks.ts).

/** An ApiError that also carries the server's per-field messages. */
export class FieldApiError extends ApiError {
  constructor(
    status: number,
    code: string | null,
    message: string,
    public readonly fieldErrors: FieldErrors | null,
  ) {
    super(status, code, message);
    this.name = 'FieldApiError';
  }
}

export interface SearchApi {
  schema(): Promise<SearchSchema>;
  savedSearches: {
    list(): Promise<SavedSearch[]>;
    create(input: { name: string; params: Record<string, unknown> }): Promise<SavedSearch>;
    update(id: string, patch: { name?: string; params?: Record<string, unknown> }): Promise<SavedSearch>;
    remove(id: string): Promise<void>;
  };
  settings: {
    get(): Promise<SettingsResponse>;
    update(values: Record<string, SettingValue>): Promise<SettingsResponse>;
    reset(keys: string[]): Promise<SettingsResponse>;
    audit(limit?: number): Promise<SettingsAuditEntry[]>;
  };
  /** POST /api/scrape — throws FieldApiError on a 400 with fieldErrors. */
  start(params: ScrapeParams): Promise<{ jobId: string }>;
  poll(jobId: string, since: number, sinceLeads: number): Promise<SearchJobPoll>;
  /** Finished job result (Activity) — carries overlap + nearMisses too. */
  job(jobId: string): Promise<SearchJobResult>;
  /** Add stored candidates to the job's folder. No new scrape, no API cost. */
  include(jobId: string, usernames: string[], source: IncludeSource): Promise<{ added: number }>;
  /** A page of the folder's leads, in either the old array or the new `{ rows }` shape. */
  folderLeadSample(folderPublicId: string): Promise<Lead[]>;
  /**
   * Editing what a scheduled search runs with. Here (not on DiscoveryClient)
   * because both throw FieldApiError with the server's per-field messages.
   */
  scheduleSettings: {
    /** What saving these params would change, without saving. */
    preview(publicId: string, params: ScrapeParams): Promise<ScheduleParamsPreview>;
    save(publicId: string, params: ScrapeParams): Promise<{ paramsChanged: boolean }>;
  };
}

export interface ScheduleParamsPreview {
  changed: boolean;
  changes: ParamChange[];
  /** The settings as they would be stored, in words. */
  paramsSummary: ParamSummaryEntry[];
}

function normaliseSavedSearch(row: Record<string, unknown>): SavedSearch {
  const params = (row.params ?? {}) as Record<string, unknown>;
  const platform = (row.platform ?? params.platform ?? null) as SavedSearch['platform'];
  return {
    // The old preset rows carry both a numeric id and a publicId; routes take the publicId.
    id: String(row.publicId ?? row.id ?? ''),
    name: String(row.name ?? ''),
    platform: platform === 'tiktok' || platform === 'instagram' || platform === 'x' ? platform : null,
    params,
    createdByName: (row.createdByName as string | null | undefined) ?? null,
    createdAt: (row.createdAt as string | null | undefined) ?? null,
    lastUsedAt: (row.lastUsedAt as string | null | undefined) ?? null,
  };
}

export function createSearchApi({ apiUrl, getToken }: ClientOptions): SearchApi {
  const base = apiUrl.replace(/\/+$/, '');

  async function call<T>(
    path: string,
    init: { method?: RequestOptions['method']; body?: unknown; query?: RequestOptions['query'] } = {},
  ): Promise<T> {
    const token = await getToken();
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    if (init.body !== undefined) headers['Content-Type'] = 'application/json';
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(init.query ?? {})) if (v !== undefined && v !== null) qs.set(k, String(v));
    const url = `${base}${path}${qs.toString() ? `?${qs}` : ''}`;
    const res = await fetch(url, {
      method: init.method ?? 'GET',
      headers,
      body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
    });
    const text = await res.text();
    const parsed: unknown = text ? safeJson(text) : null;
    if (!res.ok) {
      const payload = isErrorPayload(parsed) ? (parsed as { error: string; code?: string | null; fieldErrors?: unknown }) : null;
      const fieldErrors =
        payload && payload.fieldErrors && typeof payload.fieldErrors === 'object'
          ? (payload.fieldErrors as FieldErrors)
          : null;
      throw new FieldApiError(res.status, payload?.code ?? null, payload?.error ?? (text || `Request failed (${res.status})`), fieldErrors);
    }
    return parsed as T;
  }

  const id = encodeURIComponent;
  return {
    schema: () => call<SearchSchema>('/api/search/schema'),
    savedSearches: {
      list: async () => {
        const rows = await call<unknown>('/api/search-presets');
        return Array.isArray(rows) ? rows.map((r) => normaliseSavedSearch(r as Record<string, unknown>)) : [];
      },
      create: async (input) =>
        normaliseSavedSearch(await call<Record<string, unknown>>('/api/search-presets', { method: 'POST', body: input })),
      update: async (presetId, patch) =>
        normaliseSavedSearch(
          await call<Record<string, unknown>>(`/api/search-presets/${id(presetId)}`, { method: 'PATCH', body: patch }),
        ),
      remove: async (presetId) => {
        await call<unknown>(`/api/search-presets/${id(presetId)}`, { method: 'DELETE' });
      },
    },
    settings: {
      get: () => call<SettingsResponse>('/api/settings'),
      update: (values) => call<SettingsResponse>('/api/settings', { method: 'PUT', body: { values } }),
      reset: (keys) => call<SettingsResponse>('/api/settings/reset', { method: 'POST', body: { keys } }),
      audit: (limit = 50) => call<SettingsAuditEntry[]>('/api/settings/audit', { query: { limit } }),
    },
    start: (params) => call<{ jobId: string }>('/api/scrape', { method: 'POST', body: params }),
    poll: (jobId, since, sinceLeads) =>
      call<SearchJobPoll>(`/api/jobs/${id(jobId)}/poll`, { query: { since, sinceLeads } }),
    job: (jobId) => call<SearchJobResult>(`/api/jobs/${id(jobId)}`),
    include: (jobId, usernames, source) =>
      call<{ added: number }>(`/api/jobs/${id(jobId)}/include`, { method: 'POST', body: { usernames, source } }),
    folderLeadSample: async (folderPublicId) => {
      const res = await call<unknown>(`/api/folders/${id(folderPublicId)}/leads`, { query: { page: 1, limit: 200 } });
      if (Array.isArray(res)) return res as Lead[];
      const rows = (res as { rows?: unknown } | null)?.rows;
      return Array.isArray(rows) ? (rows as Lead[]) : [];
    },
    scheduleSettings: {
      preview: async (publicId, params) => {
        const res = await call<Record<string, unknown> | null>(`/api/schedules/${id(publicId)}/params-preview`, {
          method: 'POST',
          body: { params },
        });
        const changes = normaliseChanges(res?.changes);
        return {
          changed: res?.changed === true || changes.length > 0,
          changes,
          paramsSummary: normaliseSummary(res?.paramsSummary),
        };
      },
      save: async (publicId, params) => {
        const res = await call<Record<string, unknown> | null>(`/api/schedules/${id(publicId)}`, {
          method: 'PATCH',
          body: { params },
        });
        return { paramsChanged: res?.paramsChanged !== false };
      },
    },
  };
}
